import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Alert, useTheme, useMediaQuery } from '@mui/material';
import {
  GenderDropdown,
  VisibilityDropdown,
  TokenSummary,
  FormActions,
  TextareaForm,
  useCreateAPI,
} from '@components';
import { useCharacterStore } from '@client/store';

interface CharacterFormProps {
  character_update: boolean;
}

export const CharacterForm = React.memo(({ character_update }: CharacterFormProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    loading,
    saveLoading,
    handleDelete,
    handleSelectDraft,
    handleSubmit,
    handleClear,
  } = useCreateAPI(navigate);

  const error = useCharacterStore((state) => state.error);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        pt: { xs: 4, sm: 6, md: 8 },
        pb: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 4, md: 6 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        elevation={0}
        component="form"
        onSubmit={(e) =>
          handleSubmit(character_update ? 'Update' : 'Create', e)
        }
        sx={{
          backgroundColor: 'background.paper',
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          width: '100%',
          maxWidth: { xs: '100%', sm: '640px', md: '700px', lg: '800px' },
          p: { xs: 3, sm: 4, md: 5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: 3,
          },
        }}
      >
        <Typography
          variant={isMdUp ? 'h4' : 'h5'}
          sx={{
            color: 'text.primary',
            fontWeight: 700,
            textAlign: 'center',
            letterSpacing: '0.5px',
            mb: 1,
          }}
        >
          {character_update ? 'Update Character' : 'Create Character'}
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              fontSize: '0.9rem',
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextareaForm />
          <GenderDropdown />
          <VisibilityDropdown />
          <TokenSummary />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
            mt: 2,
          }}
        >
          <FormActions
            onClear={handleClear}
            onCreate={() =>
              handleSubmit(character_update ? 'Update' : 'Create')
            }
            onSave={() => handleSubmit('Draft')}
            onDelete={handleDelete}
            saveLoading={saveLoading}
            loading={loading}
            onSelectDraft={handleSelectDraft}
            character_update={character_update}
          />
        </Box>
      </Paper>
    </Box>
  );
});